/*
 * Copyright 2026 Sony Corporation
 */
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  type CognitoUserSession,
} from 'amazon-cognito-identity-js';

import type { AppConfig } from '../config';

/** Sign-in is SRP against the user pool directly: the pool has no hosted UI, so no token ever reaches the URL. */
export interface SignedIn {
  kind: 'signed_in';
}

/** Accounts are created by an administrator, so the first sign-in always lands here. */
export interface NewPasswordRequired {
  kind: 'new_password_required';
  complete: (newPassword: string) => Promise<SignedIn>;
}

export interface MfaRequired {
  kind: 'mfa_required';
  method: 'SOFTWARE_TOKEN_MFA' | 'SMS_MFA';
  complete: (code: string) => Promise<SignedIn>;
}

export type SignInResult = SignedIn | NewPasswordRequired | MfaRequired;

// The library calls `mfaSetup` for a pool that requires MFA; enrolment is not built, so say so rather than throw.
const MFA_SETUP_UNSUPPORTED =
  'This account must enrol a second factor, which this app cannot do yet. Ask an administrator.';

export class AuthError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AuthError';
  }
}

export interface AuthService {
  signIn: (email: string, password: string) => Promise<SignInResult>;
  signOut: () => void;
  currentUserEmail: () => string | undefined;
  /** Resolves undefined when there is no valid session; refreshes when needed. */
  getIdToken: () => Promise<string | undefined>;
}

/** The email claim of an id token, read without checking it: the token is only used to show who is signed in. */
export const emailFromIdToken = (token: string): string | undefined => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))) as { email?: unknown };
    return typeof payload.email === 'string' ? payload.email : undefined;
  } catch {
    return undefined;
  }
};

export const createAuthService = (config: AppConfig): AuthService => {
  const pool = new CognitoUserPool({ UserPoolId: config.user_pool_id, ClientId: config.user_pool_client_id });

  const signIn = (email: string, password: string): Promise<SignInResult> =>
    new Promise<SignInResult>((resolve, reject) => {
      const user = new CognitoUser({ Username: email, Pool: pool });

      const respond = (
        send: (callbacks: {
          onSuccess: (session: CognitoUserSession) => void;
          onFailure: (error: Error) => void;
          mfaSetup: () => void;
        }) => void
      ) =>
        new Promise<SignedIn>((resolveChallenge, rejectChallenge) => {
          send({
            onSuccess: () => resolveChallenge({ kind: 'signed_in' }),
            onFailure: (error) => rejectChallenge(new AuthError(error.message)),
            mfaSetup: () => rejectChallenge(new AuthError(MFA_SETUP_UNSUPPORTED)),
          });
        });

      user.authenticateUser(new AuthenticationDetails({ Username: email, Password: password }), {
        onSuccess: () => resolve({ kind: 'signed_in' }),
        onFailure: (error: Error) => reject(new AuthError(error.message)),
        mfaSetup: () => reject(new AuthError(MFA_SETUP_UNSUPPORTED)),
        newPasswordRequired: (attributes: Record<string, unknown>) => {
          // Cognito rejects these read-only attributes if they are echoed back.
          delete attributes.email_verified;
          delete attributes.email;
          resolve({
            kind: 'new_password_required',
            complete: (newPassword) =>
              respond((callbacks) => user.completeNewPasswordChallenge(newPassword, attributes, callbacks)),
          });
        },
        totpRequired: (method: string) => {
          resolve({
            kind: 'mfa_required',
            method: method === 'SMS_MFA' ? 'SMS_MFA' : 'SOFTWARE_TOKEN_MFA',
            complete: (code) => respond((callbacks) => user.sendMFACode(code, callbacks, method)),
          });
        },
        mfaRequired: (method: string) => {
          resolve({
            kind: 'mfa_required',
            method: method === 'SOFTWARE_TOKEN_MFA' ? 'SOFTWARE_TOKEN_MFA' : 'SMS_MFA',
            complete: (code) => respond((callbacks) => user.sendMFACode(code, callbacks, method)),
          });
        },
      });
    });

  const getIdToken = (): Promise<string | undefined> =>
    new Promise((resolve) => {
      const user = pool.getCurrentUser();
      if (!user) {
        resolve(undefined);
        return;
      }
      // getSession refreshes an expired id token from the refresh token.
      user.getSession((error: Error | null, session: CognitoUserSession | null) => {
        resolve(error || !session?.isValid() ? undefined : session.getIdToken().getJwtToken());
      });
    });

  /**
   * The email claim, not `getUsername()`: the pool only aliases email, so the username is a generated uuid. A session
   * that has to be refreshed answers later than this call returns, so the stored id token, which keeps its claims after
   * it has expired, is read as well.
   */
  const currentUserEmail = (): string | undefined => {
    const user = pool.getCurrentUser();
    if (!user) {
      return undefined;
    }
    let claim: string | undefined;
    user.getSession((error: Error | null, session: CognitoUserSession | null) => {
      const value = error ? undefined : session?.getIdToken().payload.email;
      claim = typeof value === 'string' ? value : undefined;
    });
    const stored = window.localStorage.getItem(
      `CognitoIdentityServiceProvider.${config.user_pool_client_id}.${user.getUsername()}.idToken`
    );
    return claim ?? (stored ? emailFromIdToken(stored) : undefined) ?? user.getUsername();
  };

  return { signIn, getIdToken, signOut: () => pool.getCurrentUser()?.signOut(), currentUserEmail };
};
